import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';
import { categories, articles, getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const currentArticles = slug ? getArticlesByCategory(slug) : articles;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const categoryName = currentCategory?.name || '全部';

  return (
    <>
      <SEOMeta
        title={`${categoryName} - 成91平台`}
        description={`浏览成91平台${categoryName}相关的所有文章内容，了解成91的最新资讯和使用指南。`}
        keywords={`成91,${categoryName},成91平台,成91${categoryName}`}
        canonicalUrl={`https://cheng91.com/category/${slug}`}
        ogTitle={`${categoryName} - 成91平台`}
        ogDescription={`浏览成91平台${categoryName}相关的所有文章内容，了解成91的最新资讯和使用指南。`}
      />

      <Navbar />

      <main className="min-h-screen pt-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: categoryName }]} />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              {categoryName}
            </h1>
            <p className="text-foreground-600 text-sm">
              共 {currentArticles.length} 篇文章
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 flex-wrap mb-10">
            <Link
              to="/category/platform-intro"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                slug === null || slug === undefined
                  ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              全部
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-14">
            {currentArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-2.5 py-1 bg-accent-100 text-accent-700 rounded-md text-xs font-medium">
                      {categories.find(c => c.slug === article.category)?.name}
                    </span>
                    <span className="text-foreground-500 text-xs">{article.date}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-foreground-600 text-sm leading-relaxed line-clamp-2 mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span>{article.author}</span>
                    <span>{article.readTime}阅读</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {currentArticles.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-background-100 rounded-full">
                <i className="ri-file-search-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}